import { DEFAULT_ELIGIBILITY_STEPS } from '../constants/ifp.constants';
import { DEFAULT_WEARABLE_ELIGIBILITY_STEPS } from '../constants/wearable.constants';
import {
  ActionLayerStatus,
  DeviceOfferDetail,
  DeviceOfferDetails,
  IfpEligibilityCheckSteps,
  IfpEligibilityType,
  InfoLayerStatus,
  InfoLayerType,
  InputOption,
  PaymentOption,
  PlanBundleOptions,
  PlanOfferDetails,
  WearableEligibilityType,
} from '../typings';

export interface ProductDetailsReducer {
  actionLayerStatus: ActionLayerStatus;
  deviceOffers: DeviceOfferDetails;
  ifpEligibility: IfpEligibilityType;
  infoLayer: InfoLayerType;
  planOffers: PlanOfferDetails;
  changePlanOffers: PlanOfferDetails;
  planBundleOption: PlanBundleOptions;
  selectedColor: string;
  selectedDevice: DeviceOfferDetail;
  paymentOptions: PaymentOption[];
  selectedStorage: string;
  storageOptions: InputOption[];
  wearableEligibility: WearableEligibilityType;
}

export const initialState: ProductDetailsReducer = {
  actionLayerStatus: ActionLayerStatus.Close,
  deviceOffers: null,
  ifpEligibility: {
    isCheckRequired: false,
    activeStep: null,
    eligibility: null,
    selectedAccountNumber: null,
    selectedLineNumber: null,
    existingOfferId: null,
    sections: DEFAULT_ELIGIBILITY_STEPS,
    isChangePlan: false,
    isChangePlanOfferDetailsLoading: false,
    ineligibleOfferIds: [],
  },
  wearableEligibility: {
    isCheckRequired: true,
    activeStep: null,
    eligibility: null,
    selectedAccountNumber: null,
    selectedLineNumber: null,
    sections: DEFAULT_WEARABLE_ELIGIBILITY_STEPS,
  },
  infoLayer: {
    status: InfoLayerStatus.Close,
  },
  planOffers: null,
  changePlanOffers: null,
  planBundleOption: PlanBundleOptions.ChooseNewPlan,
  selectedColor: null,
  selectedDevice: null,
  paymentOptions: null,
  selectedStorage: null,
  storageOptions: null,
};

export enum ProductDetailsActionTypes {
  SelectDevice = 'SELECT_DEVICE',
  SelectColor = 'SELECT_COLOR',
  SelectStorage = 'SELECT_STORAGE',
  SelectPlan = 'SELECT_PLAN',
  SelectChangePlan = 'SELECT_CHANGE_PLAN',
  HandleIneligiblePlan = 'HANDLE_INELIGIBLE_PLAN',
  SetActionLayer = 'SET_ACTION_LAYER',
  SetIfpEligibility = 'SET_IFP_ELIGIBILITY',
  SetInfoLayer = 'SET_INFO_LAYER',
  SetPlanBundleOption = 'SET_PLAN_BUNDLE_OPTION',
  SetPaymentOptions = 'SET_PAYMENT_OPTIONS',
  SetWearableEligibility = 'SET_WEARABLE_ELIGIBILITY',
}

export type ActionType =
  | {
      type: ProductDetailsActionTypes.SelectColor;
      selectedColor: string;
      selectedStorage: string;
      storageOptions: InputOption[];
    }
  | {
      type: ProductDetailsActionTypes.SelectDevice;
      selectedDeviceId?: string;
    }
  | {
      type: ProductDetailsActionTypes.SelectPlan;
      planOffers: PlanOfferDetails;
    }
  | {
      type: ProductDetailsActionTypes.SelectChangePlan;
      changePlanOffers: PlanOfferDetails;
    }
  | {
      type: ProductDetailsActionTypes.SelectStorage;
      selectedStorage: string;
    }
  | {
      type: ProductDetailsActionTypes.HandleIneligiblePlan;
      ineligibleOfferId: string;
    }
  | {
      type: ProductDetailsActionTypes.SetActionLayer;
      actionLayerStatus: ActionLayerStatus;
      ifpEligibility?: IfpEligibilityType;
      infoLayer?: InfoLayerType;
      wearableEligibility?: WearableEligibilityType;
      changePlanOffers?: PlanOfferDetails;
    }
  | {
      type: ProductDetailsActionTypes.SetIfpEligibility;
      actionLayerStatus?: ActionLayerStatus;
      ifpEligibility: IfpEligibilityType;
      planBundleOption?: PlanBundleOptions;
    }
  | {
      type: ProductDetailsActionTypes.SetInfoLayer;
      infoLayer: InfoLayerType;
    }
  | {
      type: ProductDetailsActionTypes.SetPlanBundleOption;
      planBundleOption: PlanBundleOptions;
    }
  | {
      type: ProductDetailsActionTypes.SetPaymentOptions;
      paymentOptions: PaymentOption[];
    }
  | {
      type: ProductDetailsActionTypes.SetWearableEligibility;
      actionLayerStatus?: ActionLayerStatus;
      wearableEligibility: WearableEligibilityType;
      planBundleOption?: PlanBundleOptions;
    };

export const productDetailsReducer = (
  state: ProductDetailsReducer,
  action: ActionType,
) => {
  switch (action.type) {
    case ProductDetailsActionTypes.SelectColor: {
      return {
        ...state,
        selectedColor: action.selectedColor,
        selectedStorage: action.selectedStorage,
        storageOptions: action.storageOptions,
      };
    }
    case ProductDetailsActionTypes.SelectDevice:
      return {
        ...state,
        selectedDevice: state.deviceOffers.find(
          (offer) => offer.id === action.selectedDeviceId,
        ),
      };
    case ProductDetailsActionTypes.SelectStorage: {
      return {
        ...state,
        selectedStorage: action.selectedStorage,
      };
    }
    case ProductDetailsActionTypes.SelectPlan: {
      return {
        ...state,
        planOffers: action.planOffers,
      };
    }
    case ProductDetailsActionTypes.SelectChangePlan: {
      return {
        ...state,
        changePlanOffers: action.changePlanOffers,
      };
    }
    case ProductDetailsActionTypes.HandleIneligiblePlan: {
      return {
        ...state,
        ifpEligibility: {
          ...state.ifpEligibility,
          activeStep: IfpEligibilityCheckSteps.IneligiblePlan,
          ineligibleOfferIds: [
            ...state.ifpEligibility.ineligibleOfferIds,
            action.ineligibleOfferId,
          ],
        },
      };
    }
    case ProductDetailsActionTypes.SetPlanBundleOption: {
      return {
        ...state,
        planBundleOption: action.planBundleOption,
        ifpEligibility: {
          ...state.ifpEligibility,
          isCheckRequired:
            action.planBundleOption === PlanBundleOptions.UseExistingPlan,
        },
        wearableEligibility: {
          ...state.wearableEligibility,
          isCheckRequired:
            action.planBundleOption === PlanBundleOptions.ChooseNewPlan,
        },
      };
    }
    case ProductDetailsActionTypes.SetActionLayer: {
      return {
        ...state,
        actionLayerStatus: action.actionLayerStatus,
        infoLayer: action.infoLayer ? action.infoLayer : state.infoLayer,
        ifpEligibility: {
          ...state.ifpEligibility,
          ...action.ifpEligibility,
        },
        wearableEligibility: {
          ...state.wearableEligibility,
          ...action.wearableEligibility,
        },
      };
    }
    case ProductDetailsActionTypes.SetIfpEligibility: {
      return {
        ...state,
        ifpEligibility: {
          ...state.ifpEligibility,
          ...action.ifpEligibility,
        },
        actionLayerStatus: action.actionLayerStatus
          ? action.actionLayerStatus
          : state.actionLayerStatus,
        planBundleOption: action.planBundleOption
          ? action.planBundleOption
          : state.planBundleOption,
      };
    }
    case ProductDetailsActionTypes.SetInfoLayer: {
      return {
        ...state,
        infoLayer: action.infoLayer,
      };
    }
    case ProductDetailsActionTypes.SetPaymentOptions: {
      return {
        ...state,
        paymentOptions: action.paymentOptions,
      };
    }
    case ProductDetailsActionTypes.SetWearableEligibility: {
      return {
        ...state,
        wearableEligibility: {
          ...state.wearableEligibility,
          ...action.wearableEligibility,
        },
        actionLayerStatus: action.actionLayerStatus
          ? action.actionLayerStatus
          : state.actionLayerStatus,
        planBundleOption: action.planBundleOption
          ? action.planBundleOption
          : state.planBundleOption,
      };
    }
    default:
      throw new Error();
  }
};
